import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  TextField,
} from "@mui/material";
import UploadIcon from "@mui/icons-material/Upload";
import * as React from "react";
import { useLanguage } from "../../Language";
import { useForm, Controller } from "react-hook-form";
import axios, { AxiosError } from "axios";
import { SuccessMessageControl } from "@tlc/ls2pac/app/controls/successMessageControl";

interface SavedStoredImageReturn {
  id: number;
  success: boolean;
  fileTooLarge: boolean;
}

export interface LogoUploadDialogProps extends DialogProps {
  onClose: () => void;
  onUpload: () => void;
}

export const LogoUploadDialog: React.FC<LogoUploadDialogProps> = ({
  onClose,
  onUpload,
  ...props
}) => {
  const { translate } = useLanguage();
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [uploadError, setUploadError] = React.useState<string>();
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const allowedFileTypes = [
    "image/png",
    "image/gif",
    "image/jpg",
    "image/jpeg",
    "image/jfif",
    "image/pjpeg",
    "image/pjp",
    "image/bmp",
    "image/dib",
  ];

  const onSubmit = async (data: any) => {
    setUploadError(null);
    setLoading(true);

    const file = data.file[0];
    const formData = new FormData();
    formData.append("file", file);

    try {
      const { data: responseData } = await axios.post<SavedStoredImageReturn>(
        "/storedimage/logos",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (responseData.success) {
        onUpload();
        SuccessMessageControl.show(translate("uploadImageSuccess"));
      } else {
        resetFileInput();
        setUploadError(
          responseData.fileTooLarge
            ? translate("uploadImageTooLarge")
            : translate("uploadImageError")
        );
      }
    } catch (error) {
      resetFileInput();
      setUploadError((error as AxiosError<unknown>).message); // 403 most likely
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const resetFileInput = () => {
    // we need to clear the input on error because attempting to reupload after editing the file will result in a network error
    reset({ file: null }); // Clear out the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // Clear the file input
    }
  };

  return (
    <Dialog
      {...props}
      onClose={onClose}
      slotProps={{
        paper: {
          component: "form",
          autoComplete: "off",
          noValidate: true, 
          onSubmit: (e: React.FormEvent<HTMLFormElement>) => {
            e.stopPropagation(); // Prevent triggering parent form
            e.preventDefault(); // Prevent browser's default submit behavior
            handleSubmit(onSubmit)(e);
          },
        },
        
      }}
    >
      <DialogTitle>{translate("uploadInstructions")}</DialogTitle>
      <DialogContent>
        {uploadError && <Alert severity="error">{uploadError}</Alert>}
        <Controller
          name="file"
          control={control}
          rules={{
            required: translate("fileIsRequired"),
            validate: (value: FileList) => {
              const file = value?.[0];
              const maxFileSizeInBytes = 1000000;

              if (file && !allowedFileTypes.includes(file.type)) {
                return translate("invalidFileType");
              }

              if (file.size > maxFileSizeInBytes) {
                return translate("uploadImageTooLarge");
              }

              return true;
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              disabled={loading}
              type="file"
              variant="outlined"
              margin="normal"
              fullWidth
              inputRef={fileInputRef}
              error={!!error}
              helperText={
                <>
                  {error?.message ||
                    `${translate(
                      "uploadSizeLimit"
                    )} (.png, .gif, .jpg, .jpeg, .jfif, .pjpeg, .pjp, .bmp, .dib)`}
                </>
              }
              onChange={(e) => {
                const files = (e.target as HTMLInputElement).files;
                field.onChange(files); // Capture files as FileList
                field.onBlur(); // Ensure onBlur is called
                setUploadError(null);
              }}
              slotProps={{
                htmlInput: {
                  accept: "image/png, image/jpeg, image/gif, image/bmp",
                  "aria-label": translate("logo"),
                }
              }} />
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button type="button" onClick={onClose} disabled={loading}>
          {translate("cancel")}
        </Button>
        <Button
          type="submit"
          variant="outlined"
          disabled={loading}
          startIcon={
            loading ? (
              <CircularProgress color="inherit" size={20} />
            ) : (
              <UploadIcon />
            )
          }
        >
          {translate("uploadButton")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
